import { SourcingCampaignIndex } from 'api-clients/monolith';
import { EllipsisVertical, Info, Pencil, Eye } from 'lucide-react';
import { FormattedMessage } from 'react-intl';
import { Link, useLocation } from 'react-router-dom';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DEFAULT_METRICS_FILTER_RANGE } from '@/hooks/use-metrics-filter-params';
import { useNavigationPaths } from '@/hooks/use-navigation-paths';
import { usePostHog } from '@/hooks/use-posthog';
import { REACH } from '@/types/channels';

interface MenuProps {
  campaign: SourcingCampaignIndex;
  defaultMetricsPeriod?: number | null;
}

/**
 * Deep-link to opening channels. Mirrors dashboard title-click behavior:
 * with an active hiring goal stay on hiring-goal view; otherwise land on
 * date-range view via `filter_range` (org default, or lifetime).
 */
export function openingChannelsPath(
  dashboardPath: string,
  campaign: SourcingCampaignIndex,
  defaultMetricsPeriod?: number | null,
): string {
  const base = `${dashboardPath}/${campaign.opening_slug}/channels`;
  if (campaign.has_active_hiring_goal) return base;

  const queryParams = new URLSearchParams();
  queryParams.set(
    'filter_range',
    defaultMetricsPeriod?.toString() ?? DEFAULT_METRICS_FILTER_RANGE,
  );
  return `${base}?${queryParams.toString()}`;
}

/**
 * Edit/details location from the all-campaigns table.
 *
 * Non-Reach channels stay on the nested dialog outlet
 * (`/campaigns/:channelSlug/:id/edit`). Reach uses the same full-page
 * editor as the opening's Channels page
 * (`/dashboard/:openingSlug/channels/reach/:id/edit`); `returnTo` in
 * location state sends Quit back to the campaigns table.
 */
export function campaignEditPath(
  allCampaignsPath: string,
  dashboardPath: string,
  campaign: SourcingCampaignIndex,
): string {
  if (campaign.sourcing_channel.slug === REACH) {
    return `${dashboardPath}/${campaign.opening_slug}/channels/reach/${campaign.id}/edit`;
  }

  return `${allCampaignsPath}/${campaign.sourcing_channel.slug}/${campaign.id}/edit`;
}

export function campaignEditLink(
  allCampaignsPath: string,
  dashboardPath: string,
  campaign: SourcingCampaignIndex,
  search: string,
): {
  to: { pathname: string; search?: string };
  state?: { returnTo: { pathname: string; search: string } };
} {
  const pathname = campaignEditPath(allCampaignsPath, dashboardPath, campaign);

  if (campaign.sourcing_channel.slug === REACH) {
    return {
      to: { pathname },
      state: { returnTo: { pathname: allCampaignsPath, search } },
    };
  }

  return { to: { pathname, search } };
}

export function Menu({ campaign, defaultMetricsPeriod }: MenuProps) {
  const { allCampaignsPath, dashboardPath } = useNavigationPaths();
  const { search } = useLocation();
  const posthog = usePostHog();

  const showDetails =
    campaign.is_readonly ||
    campaign.status === 'completed' ||
    campaign.status === 'failed';
  const showEdit = !showDetails;
  const editLink = campaignEditLink(
    allCampaignsPath,
    dashboardPath,
    campaign,
    search,
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger>
        <EllipsisVertical className="text-fx-neutral-600 size-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem asChild>
          <Link
            to={editLink.to}
            state={editLink.state}
            onClick={() => posthog.capture('campaigns:edit_campaign_click')}
          >
            {showEdit && (
              <>
                <Pencil className="text-fx-neutral-400 h-4 w-4" />
                <FormattedMessage
                  defaultMessage="Edit campaign"
                  description="Row menu: open the campaign's edit form"
                />
              </>
            )}
            {showDetails && (
              <>
                <Info className="text-fx-neutral-400 h-4 w-4" />
                <FormattedMessage
                  defaultMessage="Campaign details"
                  description="Row menu: open the campaign's details page"
                />
              </>
            )}
          </Link>
        </DropdownMenuItem>

        <DropdownMenuItem asChild>
          <Link
            to={openingChannelsPath(
              dashboardPath,
              campaign,
              defaultMetricsPeriod,
            )}
            onClick={() => posthog.capture('campaigns:view_opening_click')}
          >
            <Eye className="text-fx-neutral-400 h-4 w-4" />
            <FormattedMessage
              defaultMessage="View opening"
              description="Row menu: open the campaign's details page"
            />
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
